// Network switcher, mounted in each site's header rather than floating over the page.
//
// The first version was a pill docked bottom-left. On a phone that is where the content is: on
// WokeTraveler it sat on top of the Search button. A floating control on a small screen always covers
// something, so this lives in the header next to the menu, which is where an app puts account and
// workspace switchers, and it never overlaps anything.
//
// Opens a bottom sheet: thumb-reachable, dismissed by tapping away, Escape or the handle.
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Car, Tag, TrendingUp, Gamepad2, MountainSnow, Cpu, LayoutGrid, X } from 'lucide-react';
import { SiteLink } from './SiteLink';
import { SITE_BRANDS, currentSiteId } from '@/config/siteBrands';

const CONTENT_SITES = [
  { id: 'auto', to: '/auto', icon: Car, blurb: 'Prices & reviews' },
  { id: 'coupon', to: '/coupons', icon: Tag, blurb: 'Deals & coupons' },
  { id: 'finance', to: '/finance', icon: TrendingUp, blurb: 'Money & calculators' },
  { id: 'games', to: '/games', icon: Gamepad2, blurb: 'Gaming & cricket' },
  { id: 'travel', to: '/travel', icon: MountainSnow, blurb: 'Routes & stays' },
  { id: 'tech', to: '/tech', icon: Cpu, blurb: 'AI & gadgets' },
];

export default function SiteSwitcher({ className = '', label = 'More sites' }) {
  const [open, setOpen] = useState(false);
  const site = currentSiteId();

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';          // no scrolling the page behind the sheet
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [open]);

  const sheet = open && createPortal(
    <div className="fixed inset-0 z-[70]" role="dialog" aria-modal="true" aria-label="Switch site">
      <button className="absolute inset-0 w-full h-full bg-black/50 backdrop-blur-[2px]"
        onClick={() => setOpen(false)} aria-label="Close" tabIndex={-1} />
      <div
        className="absolute inset-x-0 bottom-0 bg-white rounded-t-2xl shadow-2xl max-h-[80vh] flex flex-col"
        // The cookie banner is fixed above everything until dismissed, so the sheet keeps its last
        // row clear of it (--bottom-bar-h is 0 once the visitor has chosen).
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + var(--bottom-bar-h, 0px))' }}
      >
        <div className="pt-2 pb-1 flex justify-center">
          <span className="h-1 w-10 rounded-full bg-slate-300" />
        </div>
        <div className="flex items-center justify-between px-5 pb-2">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Switch site</p>
          <button onClick={() => setOpen(false)} className="p-1 -mr-1 text-slate-400" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <ul className="overflow-y-auto overscroll-contain pb-3">
          {CONTENT_SITES.map((s) => {
            const B = SITE_BRANDS[s.id];
            const Icon = s.icon;
            const here = s.id === site;
            return (
              <li key={s.id}>
                <SiteLink to={s.to} site={s.id} onClick={() => setOpen(false)}
                  className={`flex items-center gap-3 px-5 py-3.5 ${here ? 'bg-slate-50' : 'active:bg-slate-50'}`}>
                  <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                    style={{ background: `${B.themeColor}1a`, color: B.themeColor }}>
                    <Icon style={{ width: 19, height: 19 }} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[15px] font-semibold text-slate-900 truncate">{B.name}</span>
                    <span className="block text-xs text-slate-500 truncate">{s.blurb}</span>
                  </span>
                  {here && <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Here</span>}
                </SiteLink>
              </li>
            );
          })}
        </ul>
      </div>
    </div>,
    document.body,
  );

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={label}
        title={label}
        // Inherits the header's text colour, so it works on WokeTraveler's white bar and
        // Tech-N-AI Talks' near-black one without a per-site variant.
        className={`inline-flex items-center justify-center w-9 h-9 rounded-lg text-current opacity-70 hover:opacity-100 active:scale-95 transition ${className}`}
        data-testid="site-switcher-trigger"
      >
        <LayoutGrid className="w-5 h-5" />
      </button>
      {sheet}
    </>
  );
}
